import { defineStore } from 'pinia'
import repository from "../api/repository";
import { baseLanguageCode } from '@/utils/templateLanguages'
// [matrah] these actions return { success, message } and the views toast it → no interceptor toast
import { markHandled, apiErrorMessage } from '@/utils/notify'

// [matrah] `GET /templates` now returns every template from Meta regardless of
// status. The response may be the legacy object keyed by `${name}_${language}`
// or a plain array; both are normalised into one list here so the send flows
// keep working (approved only) while the library shows everything.
function normalizeTemplates(raw) {
  const source = raw?.templates ?? raw?.data ?? raw
  let list = []
  if (Array.isArray(source)) {
    list = source
  } else if (source && typeof source === 'object') {
    list = Object.keys(source).map((key) => ({ key, ...source[key] }))
  }
  return list
    .filter((t) => t && t.name)
    .map((t) => ({
      ...t,
      key: t.key || `${t.name}_${t.language}`,
      status: t.status || 'APPROVED',
      language: t.language || '',
      components: Array.isArray(t.components) ? t.components : [],
    }))
}

export const useTemplateStore = defineStore('template', {
  state: () => {
    return {
      products: [],
      // templates: [],
      templateStatus: [],
      data: null,
      isTemplatesUpdated: true,
      // [matrah]
      templateList: [],
      isTemplatesLoading: false,
      templatesError: null,
    }
  },
  getters: {
    products: (state) => {
      return state.data ? state.data.products : [];
    },
    // [matrah] legacy:
    // templates: (state) => {
    //   return state.data ? state.data.templates : {};
    // },
    // templatesKeys: (state) => {
    //   return state.data ? Object.keys(state.data.templates) : [];
    // },
    /** Sendable templates only (approved, non-authentication), keyed by `${name}_${language}`. */
    templates: (state) => {
      const out = {}
      state.templateList.forEach((t) => {
        if (t.status === 'APPROVED' && t.category !== 'AUTHENTICATION') {
          out[t.key] = t
        }
      })
      return out
    },
    templatesKeys() {
      return Object.keys(this.templates)
    },
    /** Every template grouped by name: one row per name with its language variants. */
    templateGroups: (state) => {
      const groups = {}
      state.templateList.forEach((t) => {
        if (!groups[t.name]) {
          groups[t.name] = { name: t.name, category: t.category, languages: [] }
        }
        if (!groups[t.name].category && t.category) groups[t.name].category = t.category
        groups[t.name].languages.push({
          code: t.language,
          base: baseLanguageCode(t.language),
          status: t.status,
          id: t.id,
          components: t.components,
          key: t.key,
        })
      })
      return Object.values(groups)
        .map((g) => ({
          ...g,
          languages: g.languages.sort((a, b) => a.code.localeCompare(b.code)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    templateStatusCounts: (state) => {
      const counts = {}
      state.templateList.forEach((t) => {
        counts[t.status] = (counts[t.status] || 0) + 1
      })
      return counts
    },
    templatesWithAttachments: (state) => {

      let resData = {};
      // [matrah] legacy:
      // if(state.data){
      //   if( state.data.templates){
      //     for (const key in state.data.templates) {
      //
      //       console.log(`${key}: ${state.data.templates[key]}`);
      //       if(state.data.templates[key]['isAssetsNedded']){
      //         resData[key] = state.data.templates[key]
      //       }
      //   }
      //   }
      //
      // }
      state.templateList.forEach((t) => {
        if (t.status === 'APPROVED' && t['isAssetsNedded']) {
          resData[t.key] = t
        }
      })

    console.log({
      resData
    })

      return resData;
    },
  },
  actions: {
    async getTemplates(force = false) {
      if(!this.isTemplatesUpdated && !force){
        console.log('templates already Loaded')
      }else{
        this.isTemplatesLoading = true
        this.templatesError = null
        try {
          const res = await repository.index(`templates`);
          console.log({res})
          this.data = res.data;
          this.templateList = normalizeTemplates(res.data)

          console.log({
            templatesKeys: this.templatesKeys
          })
          // this.templates = res.data.data.templates;
          this.isTemplatesUpdated = false;
        } catch (e) {
          console.error('getTemplates', e)
          this.templatesError = e?.response?.data?.message || e.message
          throw e
        } finally {
          this.isTemplatesLoading = false
        }
      }

    },
    /** Marks the cached template list stale so the next `getTemplates()` refetches. */
    invalidateTemplates() {
      this.isTemplatesUpdated = true
    },
    /**
     * [matrah] Creates a template on Meta (one call per language, done server side).
     * Resolves `{ success, results, errors, message }`; never throws so the view can
     * render per-language outcomes.
     */
    async createTemplate(payload) {
      try {
        const res = await repository.createTemplate(payload)
        this.invalidateTemplates()
        return {
          success: true,
          results: res?.data?.results ?? [],
          message: res?.data?.message,
        }
      } catch (e) {
        console.error('createTemplate', e)
        markHandled(e)
        return {
          success: false,
          status: e?.response?.status,
          results: e?.response?.data?.results ?? [],
          errors: e?.response?.data?.errors ?? null,
          message: e?.response?.data?.message || e.message,
        }
      }
    },
    /** [matrah] Deletes a template (all its languages) by name. */
    async deleteTemplate(name) {
      try {
        const res = await repository.deleteTemplate(name)
        const success = res?.data?.success !== false
        if (success) {
          this.templateList = this.templateList.filter((t) => t.name !== name)
          this.invalidateTemplates()
        }
        return { success, message: res?.data?.message }
      } catch (e) {
        console.error('deleteTemplate', e)
        markHandled(e)
        return {
          success: false,
          // [matrah] legacy: message: e?.response?.data?.message || e.message,
          message: apiErrorMessage(e, 'Delete failed'),
        }
      }
    },
    async getTemplateStatus(filter) {
      let response = {};
      try{
        const res = await repository.create(`messages/get-template-status`, filter);
        this.templateStatus = res?.data?.data;
        console.log({res});
        response = {
          success: res?.data?.success,
          message: res?.data?.message,
        }

      }catch(e){
        console.log({e});
        markHandled(e)
        response = {
          success: e?.response?.data?.success,
          // [matrah] legacy: message: e?.response?.data?.message,
          message: apiErrorMessage(e, 'Could not load the statistics'),
        }
      }

      return response;
    }
  }
})
